import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import {
  doc,
  getDoc,
  addDoc,
  collection,
  query,
  orderBy,
  getDocs,
} from "firebase/firestore";
import {
  FiSend,
  FiLoader,
  FiCheckCircle,
  FiXCircle,
} from "react-icons/fi";

const WithdrawPage = () => {
  const [amount, setAmount] = useState("");
  const [walletAddress, setWalletAddress] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [withdrawHistory, setWithdrawHistory] = useState([]);
  const [asset, setAsset] = useState(0);

  const user = auth.currentUser;

  // Load user's total asset
  useEffect(() => {
     if (!user) {
        window.location.href = "/signin";
        return;
      }
    const fetchAsset = async () => {
      if (!user) return;
      const docRef = doc(db, "users", user.uid);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        setAsset(snapshot.data().asset || 0);
      }
    };
    fetchAsset();
  }, [user]);

  // Load withdrawal history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!user) return;
      const q = query(
        collection(db, "users", user.uid, "history"),
        orderBy("createdAt", "desc")
      );
      const querySnapshot = await getDocs(q);
      const history = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setWithdrawHistory(history);
    };

    fetchHistory();
  }, [user]);

  const handleWithdraw = async (e) => {
    e.preventDefault();
    if (!user) return;

    if (!amount || !walletAddress) {
      setMessage({ type: "error", text: "Please enter all fields." });
      return;
    }

    if (Number(amount) > asset) {
      setMessage({ type: "error", text: "Insufficient balance." });
      return;
    }

    setLoading(true);
    try {
      await addDoc(collection(db, "users", user.uid, "history"), {
        title: "Withdraw Request",
        type: "withdrawal",
        amount: Number(amount),
        walletAddress,
        status: "pending",
        applied: true,
        createdAt: new Date(),
      });

      setMessage({ type: "success", text: "Withdrawal request submitted!" });
      setAmount("");
      setWalletAddress("");
    } catch (error) {
      console.error("Error submitting withdrawal:", error);
      setMessage({ type: "error", text: "Failed to submit withdrawal." });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 p-6">
      <div className="max-w-3xl mx-auto">
        {/* Total Asset Card */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold">Total Asset</h2>
          <p className="text-3xl font-bold mt-2">{asset} USDT</p>
        </div>

        {/* Withdraw Form */}
        <div className="bg-white shadow-lg rounded-xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Withdraw USDT
          </h2>
          <form onSubmit={handleWithdraw} className="space-y-6">
            {/* Amount */}
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="peer w-full px-4 pt-5 pb-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                placeholder=" "
              />
              <label className="absolute left-4 top-2 text-gray-500 text-sm transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base">
                Amount
              </label>
            </div>

            {/* Wallet Address */}
            <div className="relative">
              <input
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="peer w-full px-4 pt-5 pb-2 border rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                placeholder=" "
              />
              <label className="absolute left-4 top-2 text-gray-500 text-sm transition-all peer-placeholder-shown:top-4 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base">
                Wallet Address
              </label>
            </div>

            {/* Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition"
            >
              {loading ? <FiLoader className="animate-spin" /> : <FiSend />}
              {loading ? "Processing..." : "Submit Withdrawal"}
            </button>
          </form>

          {/* Message */}
          {message && (
            <div
              className={`mt-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
                message.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {message.type === "success" ? <FiCheckCircle /> : <FiXCircle />}
              {message.text}
            </div>
          )}
        </div>

        {/* Withdrawal History */}
        <div className="bg-white shadow-lg rounded-xl p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">
            Withdrawal History
          </h3>
          {withdrawHistory.length === 0 ? (
            <p className="text-gray-500">No withdrawal requests yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3">Amount</th>
                    <th className="p-3">Wallet</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawHistory.map((item) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">${item.amount}</td>
                      <td className="p-3 truncate max-w-[150px]">
                        {item.walletAddress}
                      </td>
                      <td
                        className={`p-3 font-semibold ${
                          item.status === "success"
                            ? "text-green-600"
                            : item.status === "pending"
                            ? "text-yellow-600"
                            : "text-red-600"
                        }`}
                      >
                        {item.status}
                      </td>
                      <td className="p-3">
                        {item.createdAt?.toDate
                          ? item.createdAt.toDate().toLocaleString()
                          : new Date(item.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WithdrawPage;
